import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import map from 'lodash/map';
import some from 'lodash/some';
import trim from 'lodash/trim';
import { Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import {
  type ChatShareAudience,
  useChatShares,
  useCreateChatShare,
  useRevokeChatShare,
} from '../hooks/use-chat-shares';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Only the id is used for the share calls; title isn't otherwise needed by
  // this dialog. Narrower than ChatSummary so callers that don't have a full
  // chat record (e.g. Chat.page.tsx's header button) don't need to fake one.
  chat: { id: string; title?: string | null };
}

export function ShareDialog ({ open, onOpenChange, chat }: ShareDialogProps) {
  const [tab, setTab] = useState<ChatShareAudience>('any_authenticated');
  const [email, setEmail] = useState('');
  const [lastLinkUrl, setLastLinkUrl] = useState<string | null>(null);

  const { data: sharesData } = useChatShares(chat.id);
  const createShare = useCreateChatShare(chat.id);
  const revokeShare = useRevokeChatShare(chat.id);

  const shares = sharesData?.results ?? [];
  const hasActiveLink = some(shares, { audience: 'any_authenticated' });

  const handleCreateLink = () => {
    setLastLinkUrl(null);
    createShare.mutate(
      { audience: 'any_authenticated' },
      {
        onSuccess: (share) => {
          setLastLinkUrl(share.url);
          navigator.clipboard.writeText(share.url).catch(() => {});
          toast.success('Link copied to clipboard');
        },
        onError: () => toast.error('Couldn\'t create the share link'),
      },
    );
  };

  const handleSendEmail = (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!trim(email)) return;
    createShare.mutate(
      { audience: 'email', recipientEmail: trim(email) },
      {
        onSuccess: (share) => {
          setEmail('');
          toast(share.emailSent ? 'Invite sent' : 'Share created, but the invite email failed to send');
        },
        onError: () => toast.error('Couldn\'t create the share'),
      },
    );
  };

  const handleRevoke = (shareId: number) => {
    revokeShare.mutate(shareId, {
      onError: () => toast.error('Couldn\'t revoke the share'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share chat</DialogTitle>
          <DialogDescription>
            Recipients see a read-only copy of this conversation and can continue it as their own.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as ChatShareAudience)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="any_authenticated">Copy link</TabsTrigger>
            <TabsTrigger value="email">Share by email</TabsTrigger>
          </TabsList>

          <TabsContent value="any_authenticated" className="flex flex-col gap-3 pt-3">
            <p className="text-sm text-muted-foreground">
              Anyone signed in to HiLo Analytics with this link can view the chat.
            </p>
            <Button onClick={handleCreateLink} disabled={createShare.isPending}>
              {createShare.isPending
                ? (hasActiveLink ? 'Copying…' : 'Creating…')
                : (hasActiveLink ? 'Copy link' : 'Create link')}
            </Button>
            {lastLinkUrl && (
              <div className="flex gap-2">
                <Input readOnly value={lastLinkUrl} onFocus={(e) => e.currentTarget.select()} />
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(lastLinkUrl).catch(() => {});
                    toast.success('Copied');
                  }}
                >
                  Copy
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="email" className="flex flex-col gap-3 pt-3">
            <Label htmlFor="share-email">Recipient email</Label>
            <form onSubmit={handleSendEmail} className="flex gap-2">
              <Input
                id="share-email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button type="submit" disabled={createShare.isPending || !trim(email)}>
                Send
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        {shares.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <span className="text-xs font-medium text-muted-foreground">Active shares</span>
            {map(shares, (share) => (
              <div key={share.id} className="flex items-center justify-between text-sm">
                <span className="truncate text-foreground">
                  {share.audience === 'email' ? share.recipient_email : 'Anyone with the link'}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-muted-foreground hover:text-destructive"
                  aria-label="Revoke share"
                  onClick={() => handleRevoke(share.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
